import { useState, useEffect, useRef } from "react";
import {
  create,
  createOptions,
  DocumentFormat,
  HeaderFooterType,
  WrapType,
  FloatingObjectHorizontalAlignment,
  FloatingObjectHorizontalAnchorElement,
  FloatingObjectVerticalAlignment,
  FloatingObjectVerticalAnchorElement
} from 'devexpress-richedit';
import 'devextreme/dist/css/dx.light.css';
import 'devexpress-richedit/dist/dx.richedit.css';

import { getFile, saveFile } from "../../../services/backend/EditorServices";
import { debugLog } from "../../../functions";
import configOptions from "./options";
import template from "./template";
import { useSelector } from "react-redux";

const RichEditor = ({
  file,
  tags,
  dataSource,
  height,
  readOnly = false,
  noRibbon = false,
  noSave = false,
  watermark,
  onSave,
  itemEvents,
  setValidatedEditor,
  setEditorRef,
  onDocumentLoaded,
  onDocumentChanged
}) => {
  const richRef = useRef();
  const autosaveTimer = useRef(null);
  const user = useSelector((state) => state.user.value);
  const [rich, setRich] = useState();

  /**
   * Saving base64 string in Storage
   */
  const handleSaving = async (editor, evt) => {
    // A read-only or noSave editor must never persist: its file may point to
    // a shared template that would be overwritten in Storage.
    if (readOnly || noSave) return;

    try {
      const url = await saveFile(file.path, evt.base64);
      editor.hasUnsavedChanges = false;

      if (onSave) onSave(url, editor, evt);
      // Re-validate only; re-running handleDocumentLoaded here re-triggers
      // onDocumentLoaded consumers and can loop through autosave.
      handleValidateEditor(editor);
    } catch (error) {
      debugLog('Upload failed:', error);
    }
  };

  /**
   * Validate the editor tags
   */
  const handleValidateEditor = (editor) => {
    if (!setValidatedEditor) return;
    const fields = editor.document.fields._subDocument.fields;
    // Unsaved changes are no longer a blocking validation: typing and signing
    // both autosave, so a manual save is never required before registering a
    // signature. Only real tag-value gaps remain blocking.
    let validate = false;

    fields.forEach((field) => {
      const tag = editor.document.getText(field.getCodeInterval()).split(' ')[1];

      if (tags.includes(tag) && (!dataSource[tag] || dataSource[tag] === `<<${tag}>>`)) {
        validate = `Hay etiquetas en el documento que no tienen valor: <<${tag}>>`;
      }
    });
      
    setValidatedEditor(validate);
  };

  /**
   * Generate watermark base64 image with Canvas
   */
  const createWatermarkImage = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 1200;
    canvas.height = 700;
    const ctx = canvas.getContext('2d');
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(-Math.PI / 12);
    ctx.font = 'bold 150px Arial';
    ctx.fillStyle = 'rgba(200, 200, 200, 0.6)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(watermark, 0, 0);
    return canvas.toDataURL('image/png');
  };

  /**
   * Insert watermark image in document headers
   */
  const insertWatermark = (editor) => {
    try {
      const base64 = createWatermarkImage();
      const sectionCount = editor.document.sections.count;
      for (let i = 0; i < sectionCount; i++) {
        const section = editor.document.sections.getByIndex(i);
        const header = section.getHeader(HeaderFooterType.Primary, true);

        header.images.createFloating(0, {
          base64,
          actualSize: { width: 7200, height: 4000 },
          wrapType: WrapType.BehindText,
          horizontalPosition: {
            relativeTo: FloatingObjectHorizontalAnchorElement.Page,
            alignment: FloatingObjectHorizontalAlignment.Center
          },
          verticalPosition: {
            relativeTo: FloatingObjectVerticalAnchorElement.Page,
            alignment: FloatingObjectVerticalAlignment.Center
          }
        });
      }
    } catch (err) {
      console.error('[Watermark] Error:', err);
    }
  };

  /**
   * Handler when document is loaded
   */
  const handleDocumentLoaded = (editor) => {
    if (!editor.hasLoadedOnce) {
      editor.hasLoadedOnce = true;
      return;
    }

    handleValidateEditor(editor);
    if (watermark) insertWatermark(editor);
    if (onDocumentLoaded) onDocumentLoaded(editor);
  };

  /**
   * Handler when document is loaded
   */
  const handleDocumentChanged = (editor) => {
    // Re-run tag validation only (unsaved state no longer blocks).
    handleValidateEditor(editor);
    if (onDocumentChanged) onDocumentChanged(editor);

    // Debounced autosave: persist shortly after the user stops typing so the
    // signature can be registered without a manual save.
    if (!readOnly && !noSave && editor.hasUnsavedChanges) {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
      autosaveTimer.current = setTimeout(() => {
        try {
          editor.saveDocument();
        } catch (error) {
          debugLog('Autosave failed:', error);
        }
      }, 1500);
    }
  };

  /**
   * Loading Editor and content
   */
  const loadEditorAsync = async () => {
    if (rich || richRef.current.classList.contains('dxreControl')) return;

    const options = createOptions();
    configOptions.setOptions(options, itemEvents, user);
    configOptions.setEvents(options, 'saving', handleSaving);
    configOptions.setEvents(options, 'documentLoaded', handleDocumentLoaded);
    configOptions.setEvents(options, 'documentChanged', handleDocumentChanged);

    options.readOnly = readOnly;
    options.width = '100%';
    options.height = height || 'calc(100vh - 65px)';
    options.mailMerge.dataSource = [];
    if (noRibbon) options.ribbon = { visible: false };
    
    const tRich = create(document.getElementById("richEdit"), options);

    let content;
    if (file.url) {
      content = await getFile(file.url);
    } else {
      content = template;
      setTimeout(() => tRich.hasUnsavedChanges = true, 1000);
    }

    tRich.openDocument(content, 'DocumentName', DocumentFormat.OpenXml);
    if (itemEvents) tRich.royal = { events: { ...itemEvents } };
    setRich(tRich);
    if (setEditorRef) setEditorRef(tRich);
  }
  
  useEffect(() => {
    loadEditorAsync();
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    if (rich) {
      rich.mailMergeOptions._native.dataSource._items = [dataSource];
      handleValidateEditor(rich);
    }
  }, [dataSource, rich]); // eslint-disable-line

  return (
    <div>
      <div ref={richRef} id="richEdit"></div>
    </div>
  );
};

export default RichEditor;
